import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useClients } from '../../hooks/useClients';
import { useConfirm } from '../../hooks/useConfirm';
import { clientService } from '../../services/clientService';
import { ClientList } from './ClientList';
import { ClientEditor } from './ClientEditor';
import { ZohoCustomerImportModal } from './ZohoCustomerImportModal';
import type { Client } from '@shared/types';

export function ClientManager() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showZohoImport, setShowZohoImport] = useState(false);

  const { clients, loading, reload } = useClients({
    search,
    status: statusFilter as any,
  });

  const { confirm, ConfirmDialog } = useConfirm();

  const handleCreateNew = () => {
    setIsCreating(true);
  };

  // Clicking a row in the list opens the dedicated detail page rather than
  // the in-page editor panel. The detail page hosts contracts, contacts,
  // certs, and the editor itself.
  const handleEdit = (client: Client) => {
    navigate(`/clients/${client.id}`);
  };

  const handleSave = async (client: Partial<Client>) => {
    setSaving(true);
    const toastId = toast.loading('Creating client...');
    try {
      const created = await clientService.createClient(client);
      toast.success('Client created', { id: toastId });
      setIsCreating(false);
      await reload();
      // Drop the user into the new client's detail page right away.
      navigate(`/clients/${created.id}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to save client: ${errorMessage}`, { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: 'Delete Client',
      message: 'Are you sure you want to delete this client? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;

    const toastId = toast.loading('Deleting client...');
    try {
      await clientService.deleteClient(id);
      toast.success('Client deleted', { id: toastId });
      reload();
    } catch (error) {
      console.error('Failed to delete client:', error);
      toast.error('Failed to delete client.', { id: toastId });
    }
  };

  const handleCancel = () => {
    setIsCreating(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Client Management</h1>
          <p className="mt-2 text-gray-600">
            Manage your clients and customer relationships. Click any client to view contracts, contacts, and certificates.
          </p>
        </div>

        {/* When creating, swap the layout to two-column so the New Client
            editor is side-by-side with the list (matches the previous flow).
            Otherwise the list is full-width — clicking a row opens the
            detail page. */}
        <div className={isCreating ? 'grid grid-cols-1 lg:grid-cols-2 gap-8' : ''}>
          <div>
            <ClientList
              clients={clients}
              loading={loading}
              search={search}
              onSearchChange={setSearch}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              onCreateNew={handleCreateNew}
              onImportFromZohoBooks={() => setShowZohoImport(true)}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          </div>

          {isCreating && (
            <div>
              <ClientEditor
                client={null}
                onSave={handleSave}
                onCancel={handleCancel}
                saving={saving}
              />
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog />

      <ZohoCustomerImportModal
        isOpen={showZohoImport}
        onClose={() => setShowZohoImport(false)}
        onImported={() => reload()}
      />
    </div>
  );
}
